import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import 'moment/locale/ru';
import { calendarService } from '../services/calendarService';
import { tasksService } from '../services/tasksService';
import { clientsService } from '../services/clientsService';
import { objectsService } from '../services/objectsService';
import { toast } from '../utils/toast';
import TaskForm, { TASK_COLORS } from '../components/TaskForm';
import { normalizeDueDateForSubmit } from '../utils/normalizeDueDateForSubmit';
import { taskServerId } from '../utils/taskServerId';
import {
  DEFAULT_TASK_SCHEDULE,
  buildTaskDueDate,
  taskDateKey,
  taskDueDateForForm,
  taskHasTime,
  taskScheduleFormFields,
  taskScheduleParts,
} from '../utils/taskSchedule';
import './Calendar.css';

moment.locale('ru');
moment.updateLocale('ru', { week: { dow: 1, doy: 4 } });

const WEEKDAY_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const emptyForm = (dueDate = '') => ({
  title: '',
  description: '',
  client_id: '',
  object_id: '',
  due_date: dueDate,
  ...DEFAULT_TASK_SCHEDULE,
  priority: 'medium',
  cost: 0,
  category: '',
  color: TASK_COLORS[1].value,
  address: '',
  carry_items: [],
  status: 'in_progress',
  is_recurring: false,
  recurrence_type: '',
  expenses: [],
  is_personal: false,
});

export const calendarDateKey = (date) => moment(date).format('YYYY-MM-DD');

export const buildMonthGrid = (anchorDate) => {
  const first = moment(anchorDate).startOf('month').startOf('isoWeek');
  const last = moment(anchorDate).endOf('month').endOf('isoWeek');
  const days = [];
  for (const cursor = first.clone(); cursor.isSameOrBefore(last, 'day'); cursor.add(1, 'day')) {
    days.push(cursor.clone());
  }
  return days;
};

export const groupCalendarEvents = (events = []) => events.reduce((groups, event) => {
  if (!event || (event.id == null && event.server_id == null)) return groups;
  const key = taskDateKey(event.start || event.end || event.due_date) || 'undated';
  if (!groups[key]) groups[key] = [];
  groups[key].push(event);
  return groups;
}, {});

const eventStatus = (event) => {
  if (event.status === 'completed') return 'completed';
  if (event.status === 'waiting') return 'waiting';
  return 'active';
};

const eventTime = (event) => {
  if (!taskHasTime(event)) return '';
  return taskScheduleParts(event.start || event.due_date)?.time || '';
};

const sortEvents = (items = []) => [...items].sort((left, right) => {
  const leftTime = eventTime(left) || '99:99';
  const rightTime = eventTime(right) || '99:99';
  return leftTime.localeCompare(rightTime) || String(left.title || '').localeCompare(String(right.title || ''), 'ru');
});

const PlannerIcon = ({ name }) => {
  const common = {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.9,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };

  if (name === 'previous') return <svg {...common}><path d="m15 18-6-6 6-6" /></svg>;
  if (name === 'next') return <svg {...common}><path d="m9 18 6-6-6-6" /></svg>;
  if (name === 'plus') return <svg {...common}><path d="M12 5v14M5 12h14" /></svg>;
  if (name === 'clock') return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
  if (name === 'pin') return <svg {...common}><path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" /><circle cx="12" cy="10" r="2.5" /></svg>;
  if (name === 'calendar') return <svg {...common}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" /></svg>;
  return null;
};

const EventCard = ({ event, onClick, compact = false }) => {
  const status = eventStatus(event);
  const time = eventTime(event);
  const place = event.object_name || event.client_name || event.address || '';
  const color = event.color || (event.is_personal ? '#8b5cf6' : '#ff6b24');

  return (
    <button
      type="button"
      className={`planner-event planner-event-${status} ${compact ? 'planner-event-compact' : ''}`}
      style={{ '--event-color': color }}
      onClick={() => onClick(event)}
    >
      <span className="planner-event-accent" />
      <span className="planner-event-body">
        <span className="planner-event-topline">
          {time ? <span className="planner-event-time"><PlannerIcon name="clock" />{time}</span> : <span className="planner-event-time">Весь день</span>}
          {event.is_personal && <span className="planner-event-kind">Личное</span>}
          {status === 'waiting' && <span className="planner-event-status">В ожидании</span>}
          {status === 'completed' && <span className="planner-event-status">Готово</span>}
        </span>
        <strong>{event.title || 'Без названия'}</strong>
        {!compact && place && <span className="planner-event-place"><PlannerIcon name="pin" />{place}</span>}
      </span>
      <span className="planner-event-arrow">›</span>
    </button>
  );
};

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('week');
  const [anchorDate, setAnchorDate] = useState(new Date());
  const [selectedKey, setSelectedKey] = useState(calendarDateKey(new Date()));
  const [completingTaskId, setCompletingTaskId] = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [clients, setClients] = useState([]);
  const [objects, setObjects] = useState([]);
  const [formData, setFormData] = useState(emptyForm());

  const range = useMemo(() => {
    if (view === 'month') {
      const grid = buildMonthGrid(anchorDate);
      return { start: grid[0], end: grid[grid.length - 1] };
    }
    return {
      start: moment(anchorDate).startOf('isoWeek'),
      end: moment(anchorDate).endOf('isoWeek'),
    };
  }, [anchorDate, view]);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await calendarService.getEvents({
        start_date: range.start.format('YYYY-MM-DDT00:00:00'),
        end_date: range.end.format('YYYY-MM-DDT23:59:59'),
        view,
      });
      setEvents(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading calendar:', error);
      toast.error('Не удалось загрузить календарь');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [range, view]);

  useEffect(() => {
    void loadEvents();
    const handleSync = () => void loadEvents();
    window.addEventListener('crm:sync-completed', handleSync);
    return () => window.removeEventListener('crm:sync-completed', handleSync);
  }, [loadEvents]);

  useEffect(() => {
    const loadReferenceData = async () => {
      const [clientResult, objectResult] = await Promise.allSettled([
        clientsService.getAll(),
        objectsService.getAll({ status: 'active' }),
      ]);
      if (clientResult.status === 'fulfilled') setClients(clientResult.value?.data || []);
      if (objectResult.status === 'fulfilled') setObjects(objectResult.value || []);
    };
    void loadReferenceData();
  }, []);

  const groupedEvents = useMemo(() => {
    const groups = groupCalendarEvents(events);
    Object.keys(groups).forEach((key) => { groups[key] = sortEvents(groups[key]); });
    return groups;
  }, [events]);

  const selectedEvents = groupedEvents[selectedKey] || [];
  const undatedEvents = groupedEvents.undated || [];
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, index) => (
    moment(anchorDate).startOf('isoWeek').add(index, 'day')
  )), [anchorDate]);
  const monthDays = useMemo(() => buildMonthGrid(anchorDate), [anchorDate]);

  const periodTitle = view === 'month'
    ? moment(anchorDate).format('MMMM YYYY')
    : `${weekDays[0].format('D MMM')} — ${weekDays[6].format('D MMM YYYY')}`;

  const openNewTask = (dateValue = selectedKey) => {
    const dueDate = dateValue ? buildTaskDueDate(dateValue, false) : '';
    setEditingTask(null);
    setFormData(emptyForm(dueDate));
    setShowTaskForm(true);
  };

  const openTask = async (event) => {
    try {
      const task = await tasksService.getById(taskServerId(event));
      const value = task?.data || task;
      if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid task');
      setEditingTask(value);
      setFormData({
        ...emptyForm(taskDueDateForForm(value)),
        title: value.title || '',
        description: value.description || '',
        client_id: value.client_id || '',
        object_id: value.object_id || '',
        ...taskScheduleFormFields(value),
        priority: value.priority || 'medium',
        cost: value.cost || 0,
        category: value.category || '',
        color: value.color || TASK_COLORS[1].value,
        address: value.address || '',
        carry_items: Array.isArray(value.carry_items) ? value.carry_items : [],
        status: value.status || 'in_progress',
        is_recurring: Boolean(value.is_recurring),
        recurrence_type: value.recurrence_type || '',
        expenses: Array.isArray(value.expenses) ? value.expenses : [],
        is_personal: Boolean(value.is_personal),
      });
      setShowTaskForm(true);
    } catch (error) {
      console.error('Error loading task:', error);
      toast.error('Не удалось открыть задачу');
    }
  };

  const handleTaskSubmit = async (data) => {
    try {
      const submitData = { ...data };
      normalizeDueDateForSubmit(submitData);
      if (editingTask) {
        await tasksService.update(taskServerId(editingTask), submitData);
        toast.success('Задача обновлена');
      } else {
        await tasksService.create(submitData);
        toast.success('Задача создана');
      }
      setShowTaskForm(false);
      setEditingTask(null);
      await loadEvents();
      return true;
    } catch (error) {
      if (error.queuedForSync) {
        toast.success('Задача сохранена на телефоне и отправится при появлении сети');
        setShowTaskForm(false);
        setEditingTask(null);
        await loadEvents();
        return true;
      }
      toast.error(error.response?.data?.error || 'Ошибка при сохранении задачи');
      return false;
    }
  };

  const handleCompleteTask = async (id) => {
    setCompletingTaskId(id);
    try {
      const response = await tasksService.complete(id);
      const amountDue = response?.data?.amount_due || 0;
      toast.success(`Задача завершена${amountDue > 0 ? ` · к оплате ${amountDue} ₽` : ''}`);
      setShowTaskForm(false);
      setEditingTask(null);
      await loadEvents();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка при завершении задачи');
    } finally {
      setCompletingTaskId(null);
    }
  };

  const handleDeleteTask = async (id) => {
    try {
      await tasksService.delete(id);
      toast.success('Задача удалена');
      setShowTaskForm(false);
      setEditingTask(null);
      await loadEvents();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка при удалении задачи');
    }
  };

  const handleReopen = async (id) => {
    try {
      await tasksService.reopen(id);
      toast.success('Задача возвращена в работу');
      setShowTaskForm(false);
      setEditingTask(null);
      await loadEvents();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка при возврате задачи');
    }
  };

  const handleAddHistory = async (comment) => {
    if (!editingTask) return null;
    try {
      const item = await tasksService.addHistory(taskServerId(editingTask), comment);
      setEditingTask((current) => current ? { ...current, history: [item, ...(current.history || [])] } : current);
      toast.success('Заметка добавлена');
      return item;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось добавить заметку');
      throw error;
    }
  };

  const handleMarkPaid = async (id, paymentDate = null) => {
    try {
      const response = await tasksService.markPaid(id, paymentDate);
      const income = response?.data?.income || 0;
      toast.success(`Оплата сохранена${income > 0 ? ` · ${income} ₽` : ''}`);
      setShowTaskForm(false);
      setEditingTask(null);
      await loadEvents();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка при сохранении оплаты');
    }
  };

  const movePeriod = (direction) => {
    const unit = view === 'month' ? 'month' : 'week';
    const nextAnchor = moment(anchorDate).add(direction, unit).toDate();
    const nextSelected = moment(selectedKey, 'YYYY-MM-DD').add(direction, unit);
    setAnchorDate(nextAnchor);
    setSelectedKey(nextSelected.format('YYYY-MM-DD'));
  };

  const goToday = () => {
    const today = new Date();
    setAnchorDate(today);
    setSelectedKey(calendarDateKey(today));
  };

  const selectDay = (day) => {
    const key = day.format('YYYY-MM-DD');
    setSelectedKey(key);
    if (view === 'month' && !day.isSame(anchorDate, 'month')) setAnchorDate(day.toDate());
  };

  const changeView = (nextView) => {
    setView(nextView);
    setAnchorDate(moment(selectedKey, 'YYYY-MM-DD').toDate());
  };

  const selectedMoment = moment(selectedKey, 'YYYY-MM-DD');
  const selectedLabel = selectedMoment.isSame(moment(), 'day')
    ? `Сегодня, ${selectedMoment.format('D MMMM')}`
    : selectedMoment.format('dddd, D MMMM');
  const activeCount = events.filter((event) => eventStatus(event) === 'active').length;
  const waitingCount = events.filter((event) => eventStatus(event) === 'waiting').length;

  return (
    <div className="calendar-page planner-page">
      {completingTaskId && <div className="task-complete-overlay" aria-hidden="true"><span className="task-complete-check">✓</span></div>}

      <section className="planner-hero">
        <div className="planner-heading">
          <span className="planner-eyebrow">Календарь</span>
          <h1>Планировщик</h1>
          <p>{periodTitle}</p>
        </div>
        <div className="planner-summary" aria-label="Сводка периода">
          <span><b>{activeCount}</b> в работе</span>
          <span><b>{waitingCount}</b> ожидают</span>
          <span><b>{undatedEvents.length}</b> без срока</span>
        </div>
      </section>

      <section className="planner-toolbar" aria-label="Навигация календаря">
        <div className="planner-period-controls">
          <button type="button" className="planner-icon-button" onClick={() => movePeriod(-1)} aria-label="Предыдущий период"><PlannerIcon name="previous" /></button>
          <button type="button" className="planner-today-button" onClick={goToday}>Сегодня</button>
          <button type="button" className="planner-icon-button" onClick={() => movePeriod(1)} aria-label="Следующий период"><PlannerIcon name="next" /></button>
        </div>
        <div className="planner-view-switch" role="group" aria-label="Вид календаря">
          <button type="button" className={view === 'week' ? 'active' : ''} onClick={() => changeView('week')}>Неделя</button>
          <button type="button" className={view === 'month' ? 'active' : ''} onClick={() => changeView('month')}>Месяц</button>
        </div>
        <button type="button" className="planner-add-button" onClick={() => openNewTask(selectedKey)}><PlannerIcon name="plus" />Новая задача</button>
      </section>

      {loading && events.length === 0 ? (
        <div className="planner-loading"><span className="spinner" /><p>Собираю расписание…</p></div>
      ) : (
        <div className={`planner-workspace planner-${view}-workspace ${loading ? 'planner-refreshing' : ''}`}>
          <section className="planner-calendar-surface">
            {view === 'week' ? (
              <div className="planner-week-strip">
                {weekDays.map((day, index) => {
                  const key = day.format('YYYY-MM-DD');
                  const dayEvents = groupedEvents[key] || [];
                  const isSelected = key === selectedKey;
                  const isToday = day.isSame(moment(), 'day');
                  return (
                    <button
                      type="button"
                      key={key}
                      className={`planner-day-tab ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''}`}
                      onClick={() => selectDay(day)}
                    >
                      <span>{WEEKDAY_SHORT[index]}</span>
                      <strong>{day.format('D')}</strong>
                      <small>{day.format('MMM')}</small>
                      <i>{dayEvents.length || ''}</i>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="planner-month">
                <div className="planner-month-weekdays">{WEEKDAY_SHORT.map((day) => <span key={day}>{day}</span>)}</div>
                <div className="planner-month-grid">
                  {monthDays.map((day) => {
                    const key = day.format('YYYY-MM-DD');
                    const dayEvents = groupedEvents[key] || [];
                    const isSelected = key === selectedKey;
                    const isToday = day.isSame(moment(), 'day');
                    const outside = !day.isSame(anchorDate, 'month');
                    return (
                      <button
                        type="button"
                        key={key}
                        className={`planner-month-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${outside ? 'outside' : ''}`}
                        onClick={() => selectDay(day)}
                        onDoubleClick={() => openNewTask(key)}
                        aria-label={`${day.format('D MMMM')}, задач: ${dayEvents.length}`}
                      >
                        <span className="planner-month-date">{day.format('D')}</span>
                        <span className="planner-month-events">
                          {dayEvents.slice(0, 3).map((event) => (
                            <span key={event.id || event.server_id} className={`planner-month-event planner-month-event-${eventStatus(event)}`} style={{ '--event-color': event.color || '#ff6b24' }}>
                              <i />{event.title || 'Без названия'}
                            </span>
                          ))}
                          {dayEvents.length > 3 && <small>ещё {dayEvents.length - 3}</small>}
                        </span>
                        {dayEvents.length > 0 && <span className="planner-month-count">{dayEvents.length}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          <aside className="planner-agenda">
            <header className="planner-agenda-header">
              <div>
                <span>Выбранный день</span>
                <h2>{selectedLabel}</h2>
              </div>
              <button type="button" onClick={() => openNewTask(selectedKey)} aria-label="Добавить задачу на выбранный день"><PlannerIcon name="plus" /></button>
            </header>
            <div className="planner-agenda-list">
              {selectedEvents.length > 0 ? selectedEvents.map((event) => (
                <EventCard key={event.id || event.server_id} event={event} onClick={openTask} />
              )) : (
                <button type="button" className="planner-empty-day" onClick={() => openNewTask(selectedKey)}>
                  <span><PlannerIcon name="calendar" /></span>
                  <strong>День свободен</strong>
                  <small>Нажмите, чтобы запланировать задачу</small>
                </button>
              )}
            </div>
          </aside>
        </div>
      )}

      <section className="planner-undated">
        <div className="planner-undated-heading">
          <div><span>Без срока</span><h2>Когда-нибудь</h2></div>
          <button type="button" onClick={() => openNewTask(null)}><PlannerIcon name="plus" />Добавить</button>
        </div>
        {undatedEvents.length > 0 ? (
          <div className="planner-undated-list">
            {undatedEvents.map((event) => <EventCard key={event.id || event.server_id} event={event} onClick={openTask} compact />)}
          </div>
        ) : <p className="planner-undated-empty">Все задачи разложены по датам — редкая, почти мифическая красота.</p>}
      </section>

      {showTaskForm && (
        <TaskForm
          formData={formData}
          setFormData={setFormData}
          clients={clients}
          objects={objects}
          onSubmit={handleTaskSubmit}
          onCancel={() => { setShowTaskForm(false); setEditingTask(null); }}
          onComplete={handleCompleteTask}
          onDelete={handleDeleteTask}
          onReopen={handleReopen}
          onAddHistory={handleAddHistory}
          onMarkPaid={handleMarkPaid}
          editingTask={editingTask}
        />
      )}
    </div>
  );
};

export default Calendar;
